import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sortBy } from 'lodash';
import type { OrganizationReference } from '@trinnvis/dabih-api-client';
import type { User } from '../../store/types.js';
import { getOrganizationUrl } from 'src/store/selectors';
import { t } from 'src/i18n';

/**
 * Organizations list with Leave and Delete buttons for /me page
 * Shows all organizations with management options
 */
@customElement('d-me-organizations')
export class DMeOrganizations extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }

    #accountSelector ul {
      list-style-type: none;
      margin: 0 0 12px 0;
      font-weight: 400;
    }

    #accountSelector li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 9px 0 8px 0;
      border-bottom: 1px solid var(--borderColorTransparent);
    }

    #accountSelector li:first-child {
      border-top: 1px solid var(--borderColorTransparent);
    }

    .organization-info {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .organization-link {
      color: var(--linkColorGray);
      text-decoration: none;
      cursor: pointer;
      margin-bottom: 4px;
    }

    .organization-link:hover {
      color: black;
    }

    .organization-details {
      font-size: 12px;
      color: #666;
    }

    .organization-actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    button {
      padding: 6px 12px;
      font-family: var(--small);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border: 1px solid;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
      background: white;
    }

    button.leave {
      color: var(--themeColor);
      border-color: var(--themeColor);
    }

    button.leave:hover:not(:disabled) {
      background: var(--themeColor);
      color: white;
    }

    button.delete {
      color: #dc3545;
      border-color: #dc3545;
    }

    button.delete:hover:not(:disabled) {
      background: #dc3545;
      color: white;
    }

    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .owner-notice {
      font-size: 11px;
      color: #666;
      font-style: italic;
    }

    .no-org-message {
      padding: 20px;
      text-align: center;
      color: #666;
    }

    .add-organization {
      padding: 6px 0;
      font-family: var(--small);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: white;
      cursor: pointer;
      text-decoration: none;
      display: inline-block;
    }
  `;

  @property({ type: Array })
  organizations: OrganizationReference[] = [];

  @property({ type: Object })
  user!: User;

  @state()
  private processingOrgs = new Set<number>();

  get sortedOrganizations() {
    const a = this.organizations.slice();
    return sortBy(a, [(item) => (item.name ? item.name.toUpperCase() : '')]);
  }

  private isOwner(organization: OrganizationReference): boolean {
    // Check if current user is the owner
    return organization.ownerEmail === this.user.username;
  }

  private isTestOrganization(organization: OrganizationReference): boolean {
    // Test organizations are those where the owner email is from test domains
    // API checks contactEmail for: @mailinator.com, dabih.test+*@gmail.com, @thisisnotmyrealemail.com
    // We approximate this by checking ownerEmail since contactEmail is not available in OrganizationReference
    if (!organization.ownerEmail) return false;
    const email = organization.ownerEmail.toLowerCase();
    return (
      email.endsWith('@mailinator.com') ||
      (email.startsWith('dabih.test+') && email.endsWith('@gmail.com')) ||
      email.endsWith('@thisisnotmyrealemail.com')
    );
  }

  private async handleLeave(organization: OrganizationReference) {
    if (!confirm(t('account.meOrganizations.confirmLeave', { name: organization.name }))) {
      return;
    }

    this.processingOrgs.add(organization.id);
    this.requestUpdate();

    try {
      // Call API to leave organization
      this.dispatchEvent(
        new CustomEvent('leave-organization', {
          bubbles: true,
          composed: true,
          detail: { organizationId: organization.id },
        }),
      );
    } finally {
      this.processingOrgs.delete(organization.id);
      this.requestUpdate();
    }
  }

  private async handleDelete(organization: OrganizationReference) {
    const confirmMessage = t('account.meOrganizations.confirmDelete', { name: organization.name });
    if (!confirm(confirmMessage)) {
      return;
    }

    if (!confirm(t('account.meOrganizations.confirmDeleteLastChance'))) {
      return;
    }

    this.processingOrgs.add(organization.id);
    this.requestUpdate();

    try {
      // Call API to delete organization
      this.dispatchEvent(
        new CustomEvent('delete-organization', {
          bubbles: true,
          composed: true,
          detail: { organizationId: organization.id },
        }),
      );
    } finally {
      this.processingOrgs.delete(organization.id);
      this.requestUpdate();
    }
  }

  render() {
    return html`
      <div id="accountSelector">
        <ul>
          ${this.sortedOrganizations.map((organization) => {
            const isOwner = this.isOwner(organization);
            const isTest = this.isTestOrganization(organization);
            const isProcessing = this.processingOrgs.has(organization.id);

            return html`
              <li>
                <div class="organization-info">
                  <a class="organization-link" href="${getOrganizationUrl(organization)}">
                    ${organization.name}${organization.closed ? t('account.meOrganizations.closedSuffix') : ''}
                  </a>
                  <div class="organization-details">
                    ${organization.ownerEmail}
                    ${isOwner
                      ? html`<span class="owner-notice">${t('account.meOrganizations.youAreOwner')}</span>`
                      : nothing}
                  </div>
                </div>
                <div class="organization-actions">
                  ${isOwner
                    ? html` <span class="owner-notice">${t('account.meOrganizations.changeAdminBeforeLeaving')}</span> `
                    : nothing}
                  <button
                    class="leave"
                    ?disabled=${isOwner || isProcessing}
                    @click=${(e: Event) => {
                      e.preventDefault();
                      this.handleLeave(organization);
                    }}
                    title=${isOwner
                      ? t('account.meOrganizations.mustChangeAdminFirst')
                      : t('account.meOrganizations.leaveOrganization')}
                  >
                    ${t('account.meOrganizations.leave')}
                  </button>
                  ${isTest && isOwner
                    ? html`
                        <button
                          class="delete"
                          ?disabled=${isProcessing}
                          @click=${(e: Event) => {
                            e.preventDefault();
                            this.handleDelete(organization);
                          }}
                          title=${t('account.meOrganizations.deleteTestOrganization')}
                        >
                          ${t('account.meOrganizations.delete')}
                        </button>
                      `
                    : nothing}
                </div>
              </li>
            `;
          })}
          ${this.organizations.length === 0
            ? html` <li class="no-org-message">${t('account.meOrganizations.noOrganizations')}</li> `
            : nothing}
        </ul>
        <a class="add-organization" href="/signup">${t('account.meOrganizations.addNewOrganization')}</a>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-me-organizations': DMeOrganizations;
  }
}
